const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000'

const USE_MOCK = import.meta.env.VITE_MOCK_API === 'true'

class ApiService {
  constructor() {
    this.baseURL = API_BASE
  }

  getAuthHeaders() {
    const token = localStorage.getItem('jwt')
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    }
  }

  async request(endpoint, options = {}) {
    if (USE_MOCK) {
      return this.mockRequest(endpoint, options)
    }

    const url = `${this.baseURL}${endpoint}`
    const config = {
      headers: this.getAuthHeaders(),
      ...options
    }

    try {
      const response = await fetch(url, config)

      let data
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        data = await response.json()
      } else {
        const text = await response.text()
        data = text ? { error: text } : {}
      }

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`)
      }

      return data
    } catch (error) {
      console.error('API request failed:', error)
      throw error
    }
  }

  async mockRequest(endpoint, options = {}) {
    const method = (options.method || 'GET').toUpperCase()
    const delay = (ms) => new Promise(r => setTimeout(r, ms))
    await delay(200)

    const getStoredUser = () => {
      try { return JSON.parse(localStorage.getItem('mock_user') || 'null') } catch { return null }
    }
    const setStoredUser = (u) => localStorage.setItem('mock_user', JSON.stringify(u))

    if (endpoint === '/api/auth/me') {
      const token = localStorage.getItem('jwt')
      const user = getStoredUser()
      if (token && user) {
        return { success: true, user }
      }
      return { success: false, error: 'Not authenticated' }
    }

    if (endpoint === '/api/auth/profile' && method === 'PUT') {
      const updates = JSON.parse(options.body || '{}')
      const user = getStoredUser()
      if (!user) return { success: false, error: 'Not authenticated' }
      const updated = { ...user, ...updates }
      setStoredUser(updated)
      return { success: true, user: updated, message: 'Profile updated' }
    }

    if (endpoint === '/api/auth/logout' && method === 'POST') {
      localStorage.removeItem('jwt')
      return { success: true }
    }

    if (endpoint.startsWith('/api/pages') && method === 'GET') {
      if (endpoint === '/api/pages/templates/list') {
        return { success: true, templates: [] }
      }
      return { success: true, pages: [] }
    }

    if (endpoint.startsWith('/api/pages') && method === 'POST') {
      const body = JSON.parse(options.body || '{}')
      return { success: true, page: { id: 'page_mock', ...body } }
    }

    return { success: false, error: `Mock not implemented for ${method} ${endpoint}` }
  }

  // =============================================================================
  // Auth endpoints (simplified - login/register handled by Chatwoot SSO)
  // =============================================================================

  async getUserProfile() {
    return this.request('/api/auth/me')
  }

  async updateProfile(firstName, lastName) {
    return this.request('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify({ firstName, lastName })
    })
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword })
    })
  }

  async clearDealPrefillPending() {
    return this.request('/api/auth/active-deal/prefill-applied', {
      method: 'POST'
    })
  }

  async unlinkActiveDeal() {
    return this.request('/api/auth/active-deal', {
      method: 'DELETE'
    })
  }

  async logout() {
    return this.request('/api/auth/logout', {
      method: 'POST'
    })
  }

  // =============================================================================
  // Template endpoints
  // =============================================================================

  async getTemplates() {
    return this.request('/api/templates')
  }

  async createTemplate(templateData) {
    return this.request('/api/templates', {
      method: 'POST',
      body: JSON.stringify(templateData)
    })
  }

  async updateTemplate(id, templateData) {
    return this.request(`/api/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(templateData)
    })
  }

  async deleteTemplate(id) {
    return this.request(`/api/templates/${id}`, {
      method: 'DELETE'
    })
  }

  // AI Website Generation endpoint
  async generateWebsite(prompt, style = 'professional') {
    return this.request('/api/ai/generate-website', {
      method: 'POST',
      body: JSON.stringify({ prompt, style })
    })
  }

  // =============================================================================
  // Page Management API
  // =============================================================================

  async getPages() {
    return this.request('/api/pages')
  }

  async getPage(pageId) {
    return this.request(`/api/pages/${pageId}`)
  }

  async createPage(pageData) {
    return this.request('/api/pages', {
      method: 'POST',
      body: JSON.stringify(pageData)
    })
  }

  async updatePage(pageId, pageData) {
    return this.request(`/api/pages/${pageId}`, {
      method: 'PUT',
      body: JSON.stringify(pageData)
    })
  }

  async deletePage(pageId) {
    return this.request(`/api/pages/${pageId}`, {
      method: 'DELETE'
    })
  }

  async duplicatePage(pageId) {
    return this.request(`/api/pages/${pageId}/duplicate`, {
      method: 'POST'
    })
  }

  async reorderPages(pages) {
    return this.request('/api/pages/reorder', {
      method: 'PUT',
      body: JSON.stringify({ pages })
    })
  }

  async updatePageStatus(pageId, status) {
    return this.request(`/api/pages/${pageId}/publish`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    })
  }

  async getPageTemplates() {
    return this.request('/api/pages/templates/list')
  }

  // =============================================================================
  // Domain Management API
  // =============================================================================

  async getDomains() {
    return this.request('/api/domains')
  }

  async getDomain(domainId) {
    return this.request(`/api/domains/${domainId}`)
  }

  async createDomain(domainData) {
    return this.request('/api/domains', {
      method: 'POST',
      body: JSON.stringify(domainData)
    })
  }

  async updateDomain(domainId, domainData) {
    return this.request(`/api/domains/${domainId}`, {
      method: 'PUT',
      body: JSON.stringify(domainData)
    })
  }

  async deleteDomain(domainId) {
    return this.request(`/api/domains/${domainId}`, {
      method: 'DELETE'
    })
  }

  async verifyDNS(domainId) {
    return this.request(`/api/domains/${domainId}/verify-dns`, {
      method: 'POST'
    })
  }

  async verifySSL(domainId) {
    return this.request(`/api/domains/${domainId}/verify-ssl`, {
      method: 'POST'
    })
  }

  async setPrimaryDomain(domainId) {
    return this.request(`/api/domains/${domainId}/set-primary`, {
      method: 'POST'
    })
  }

  async getDNSRecords(domainId) {
    return this.request(`/api/domains/${domainId}/dns-records`)
  }

  async addDNSRecord(domainId, recordData) {
    return this.request(`/api/domains/${domainId}/dns-records`, {
      method: 'POST',
      body: JSON.stringify(recordData)
    })
  }

  async deleteDNSRecord(domainId, recordId) {
    return this.request(`/api/domains/${domainId}/dns-records/${recordId}`, {
      method: 'DELETE'
    })
  }

  // =============================================================================
  // Website Forms API
  // =============================================================================

  async getWebsiteForms() {
    return this.request('/api/website-forms')
  }

  async getWebsiteForm(formId) {
    return this.request(`/api/website-forms/${formId}`)
  }

  async createWebsiteForm(formData) {
    return this.request('/api/website-forms', {
      method: 'POST',
      body: JSON.stringify(formData)
    })
  }

  async updateWebsiteForm(formId, formData) {
    return this.request(`/api/website-forms/${formId}`, {
      method: 'PUT',
      body: JSON.stringify(formData)
    })
  }

  async deleteWebsiteForm(formId) {
    return this.request(`/api/website-forms/${formId}`, {
      method: 'DELETE'
    })
  }

  async submitWebsiteForm(formId, data, honeypot = null, captchaToken = null) {
    return this.request(`/api/website-forms/${formId}/submit`, {
      method: 'POST',
      body: JSON.stringify({ data, honeypot, captchaToken })
    })
  }

  async getFormSubmissions(formId, options = {}) {
    const params = new URLSearchParams()
    if (options.page) params.append('page', options.page)
    if (options.limit) params.append('limit', options.limit)
    if (options.status) params.append('status', options.status)
    if (options.isAuthenticated !== undefined) params.append('isAuthenticated', options.isAuthenticated)

    const queryString = params.toString()
    return this.request(`/api/website-forms/${formId}/submissions${queryString ? `?${queryString}` : ''}`)
  }

  async moderateSubmission(formId, submissionId, status, notes) {
    return this.request(`/api/website-forms/${formId}/submissions/${submissionId}`, {
      method: 'PUT',
      body: JSON.stringify({ status, notes })
    })
  }

  // =============================================================================
  // Google Analytics endpoints
  // =============================================================================

  async getGoogleAnalyticsConfig(orgId) {
    return this.request(`/api/organizations/${orgId}/google-analytics`)
  }

  async updateGoogleAnalyticsConfig(orgId, config) {
    return this.request(`/api/organizations/${orgId}/google-analytics`, {
      method: 'PUT',
      body: JSON.stringify(config)
    })
  }

  async testGoogleAnalyticsConfig(orgId) {
    return this.request(`/api/organizations/${orgId}/google-analytics/test`, {
      method: 'POST'
    })
  }

  // =============================================================================
  // Facebook Pixel endpoints
  // =============================================================================

  async getFacebookPixelConfig(orgId) {
    return this.request(`/api/organizations/${orgId}/facebook-pixel`)
  }

  async updateFacebookPixelConfig(orgId, config) {
    return this.request(`/api/organizations/${orgId}/facebook-pixel`, {
      method: 'PUT',
      body: JSON.stringify(config)
    })
  }

  async testFacebookPixelConfig(orgId) {
    return this.request(`/api/organizations/${orgId}/facebook-pixel/test`, {
      method: 'POST'
    })
  }

  // =============================================================================
  // Repliers API - Live Property Data
  // =============================================================================

  /**
   * Get property listings with optional filters
   * @param {Object} options - Filter options
   * @param {number} options.limit - Number of listings (default: 12)
   * @param {string} options.city - Filter by city
   * @param {number} options.minPrice - Minimum price
   * @param {number} options.maxPrice - Maximum price
   * @param {string} options.propertyType - Type of property
   * @param {number} options.bedrooms - Minimum bedrooms
   * @param {number} options.bathrooms - Minimum bathrooms
   * @param {string} options.sortBy - Sort order
   * @param {string} options.format - 'raw' or 'formatted'
   */
  async getRepliersListings(options = {}) {
    const params = new URLSearchParams()
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value)
      }
    })
    const queryString = params.toString()
    return this.request(`/api/repliers/listings${queryString ? `?${queryString}` : ''}`)
  }

  /**
   * Get a single listing by MLS number
   * @param {string} mlsNumber - The MLS number
   * @param {string} format - 'raw' or 'formatted'
   */
  async getRepliersListing(mlsNumber, format = 'formatted') {
    return this.request(`/api/repliers/listings/${mlsNumber}?format=${format}`)
  }

  /**
   * Search listings by query
   * @param {string} query - Search query
   * @param {Object} options - Additional filter options
   */
  async searchRepliersListings(query, options = {}) {
    const params = new URLSearchParams({ q: query })
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value)
      }
    })
    return this.request(`/api/repliers/search?${params.toString()}`)
  }

  /**
   * Get featured/hot listings
   * @param {number} limit - Number of featured listings
   */
  async getRepliersFeaturedListings(limit = 6) {
    return this.request(`/api/repliers/featured?limit=${limit}`)
  }

  /**
   * Get listings formatted for template injection
   * @param {Object} options - Options including template style
   */
  async getRepliersTemplateData(options = {}) {
    const params = new URLSearchParams()
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value)
      }
    })
    const queryString = params.toString()
    return this.request(`/api/repliers/template-data${queryString ? `?${queryString}` : ''}`)
  }
}

// Create API service instance
const apiService = new ApiService()

// Export page-specific API methods
export const pageAPI = {
  getPages: () => apiService.getPages(),
  getPage: (pageId) => apiService.getPage(pageId),
  createPage: (pageData) => apiService.createPage(pageData),
  updatePage: (pageId, pageData) => apiService.updatePage(pageId, pageData),
  deletePage: (pageId) => apiService.deletePage(pageId),
  duplicatePage: (pageId) => apiService.duplicatePage(pageId),
  reorderPages: (pages) => apiService.reorderPages(pages),
  updatePageStatus: (pageId, status) => apiService.updatePageStatus(pageId, status),
  getPageTemplates: () => apiService.getPageTemplates()
}

// Export domain-specific API methods
export const domainAPI = {
  getDomains: () => apiService.getDomains(),
  getDomain: (domainId) => apiService.getDomain(domainId),
  createDomain: (domainData) => apiService.createDomain(domainData),
  updateDomain: (domainId, domainData) => apiService.updateDomain(domainId, domainData),
  deleteDomain: (domainId) => apiService.deleteDomain(domainId),
  verifyDNS: (domainId) => apiService.verifyDNS(domainId),
  verifySSL: (domainId) => apiService.verifySSL(domainId),
  setPrimaryDomain: (domainId) => apiService.setPrimaryDomain(domainId),
  getDNSRecords: (domainId) => apiService.getDNSRecords(domainId),
  addDNSRecord: (domainId, recordData) => apiService.addDNSRecord(domainId, recordData),
  deleteDNSRecord: (domainId, recordId) => apiService.deleteDNSRecord(domainId, recordId)
}

// Export Repliers API methods for live property data
export const repliersAPI = {
  getListings: (options) => apiService.getRepliersListings(options),
  getListing: (mlsNumber, format) => apiService.getRepliersListing(mlsNumber, format),
  search: (query, options) => apiService.searchRepliersListings(query, options),
  getFeatured: (limit) => apiService.getRepliersFeaturedListings(limit),
  getTemplateData: (options) => apiService.getRepliersTemplateData(options)
}

export default apiService
